/**
 * The People page's toast (§6.2): a line at the foot of the screen, with
 * Copy link in mock mode, where the invite's link isn't emailed. It goes
 * after 8 seconds, or when closed.
 */
import { Button } from "@crew/ui/components/button";
import { X } from "lucide-react";
import { useEffect, useState } from "react";

export type ToastMessage = { id: number; text: string; link?: string };

/** One toast: the page gives each message its own key, so Copied starts over. */
export function Toast({ message, onClose }: { message: ToastMessage; onClose: () => void }) {
  const [copied, setCopied] = useState(false);
  useEffect(() => {
    const timer = setTimeout(onClose, 8000);
    return () => clearTimeout(timer);
  }, [onClose]);
  const copy = async () => {
    if (!message.link) return;
    await navigator.clipboard.writeText(message.link).catch(() => {});
    setCopied(true);
  };
  return (
    <div
      role="status"
      data-people-toast
      {...(message.link ? { "data-invite-link": message.link } : {})}
      className="fixed bottom-[calc(5rem+env(safe-area-inset-bottom))] left-1/2 z-50 flex w-max max-w-[calc(100%-2rem)] -translate-x-1/2 items-center gap-3 rounded-lg bg-foreground py-2 pr-2 pl-4 text-sm text-background shadow-md md:bottom-6"
    >
      <span className="min-w-0">{message.text}</span>
      {message.link ? (
        <Button
          size="sm"
          variant="ghost"
          className="text-background hover:bg-background/10 hover:text-background"
          onClick={() => void copy()}
        >
          {copied ? "Copied" : "Copy link"}
        </Button>
      ) : null}
      <Button
        size="icon-sm"
        variant="ghost"
        aria-label="Close"
        className="text-background/70 hover:bg-background/10 hover:text-background"
        onClick={onClose}
      >
        <X />
      </Button>
    </div>
  );
}
